import { AnimatePresence, motion } from 'motion/react';
import { Building, Edit2, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { Asset } from '../lib/types';
import { cn, formatCurrency } from '../lib/utils';

interface AssetsTabProps {
  assets: Asset[];
  localCurrency: string;
  onManageAssets: () => void;
  onEditAsset: (asset: Asset) => void;
  onDeleteAsset: (id: string) => void;
  isPrivacyMode: boolean;
}

export function AssetsTab({
  assets,
  localCurrency,
  onManageAssets,
  onEditAsset,
  onDeleteAsset,
  isPrivacyMode,
}: AssetsTabProps) {
  const totalAssetsValue = assets.reduce((sum, asset) => sum + asset.value, 0);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between px-1">
        <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
          Assets
        </h2>
        <button
          onClick={onManageAssets}
          className="flex items-center gap-2 rounded-xl bg-zinc-900 px-4 py-2 text-xs font-bold tracking-wider text-white uppercase shadow-sm transition-opacity hover:opacity-90 dark:bg-zinc-100 dark:text-zinc-900"
        >
          <Plus className="h-4 w-4" />
          Add
        </button>
      </div>

      <div className="rounded-[32px] bg-zinc-900 p-8 text-white shadow-2xl shadow-zinc-200 transition-colors dark:bg-zinc-800 dark:shadow-zinc-900/50">
        <div className="space-y-2 text-center">
          <p className="text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
            Total Asset Value
          </p>
          <h2
            className={cn(
              'text-4xl font-bold tracking-tight text-emerald-400',
              isPrivacyMode && 'blur-md select-none'
            )}
          >
            {formatCurrency(totalAssetsValue, localCurrency)}
          </h2>
        </div>
      </div>

      <div className="space-y-4">
        {assets.length === 0 ? (
          <div className="py-12 text-center text-sm text-zinc-400 italic">
            <Building className="mx-auto mb-2 h-8 w-8 opacity-50" />
            You have no assets. Add one to start tracking.
          </div>
        ) : (
          assets.map((asset) => (
            <div
              key={asset.id}
              className="rounded-3xl border border-zinc-100 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900"
            >
              {asset.picture && (
                <div
                  onClick={() => setSelectedImage(asset.picture || null)}
                  className="mb-4 h-32 w-full cursor-zoom-in overflow-hidden rounded-2xl bg-zinc-100 transition-transform active:scale-[0.99] dark:bg-zinc-950"
                >
                  <img
                    src={asset.picture}
                    alt={asset.name}
                    className="h-full w-full object-cover"
                  />
                </div>
              )}
              <div className="flex items-end justify-between">
                <div className="mr-4 flex-1">
                  <p className="font-bold text-zinc-900 dark:text-zinc-100">
                    {asset.name}
                  </p>
                  <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                    <span
                      className={cn(
                        'font-bold text-emerald-500',
                        isPrivacyMode && 'blur-sm select-none'
                      )}
                    >
                      {formatCurrency(asset.value, localCurrency)}
                    </span>
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="rounded-lg bg-zinc-100 px-2 py-1 text-xs font-medium text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
                    {asset.type}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onEditAsset(asset)}
                      className="rounded-xl p-2 text-zinc-400 transition-colors hover:bg-indigo-50 hover:text-indigo-500 dark:hover:bg-indigo-900/20"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onDeleteAsset(asset.id)}
                      className="rounded-xl p-2 text-zinc-400 transition-colors hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-rose-900/20"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-50 rounded-full bg-zinc-800 p-2 text-white transition-colors hover:bg-zinc-700"
            >
              <X className="h-6 w-6" />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              src={selectedImage}
              alt="Full view"
              className="max-h-[85vh] max-w-full rounded-2xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
